import { Box, TextField, Typography } from '@mui/material';
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import { Employees } from '~/data/employees/types';
import { OrganizationUnit } from '~/data/orgunits/types';
import { useEmployeeActivityLog } from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/useEmployeeActivityLog';
import { useUserActivityLogGridData } from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/useEmployeeActivityLogGridData';
import { UserActivityEventPayloadDtoTarget } from '~/services/Http/client.iam.generated';
import { theme } from '~/theme';

export const EmployeeActivityLog = ({ employeeId }: { employeeId: number }) => {
  const { t } = useTranslation();

  const { userActivity, dateRange, onRangeChange } =
    useEmployeeActivityLog(employeeId);
  const { gridData, resolveEmployeeAndUnit } = useUserActivityLogGridData({
    userActivity,
  });

  const EmployeeName = (props: {
    employee: Employees;
    unit: OrganizationUnit;
  }) => {
    const { employee, unit } = props;

    return (
      <>
        {`${employee?.fEmpFirstName} ${employee?.fEmpLastName}`}
        <Typography
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.8em',
            marginLeft: 1,
          }}
          component={'span'}
        >
          {unit?.fOrgUnitName}
        </Typography>
      </>
    );
  };

  const columnsConfig: GridColDef[] = [
    {
      field: 'userName',
      headerName: t('name'),
      minWidth: 150,
    },
    {
      field: 'userUnit',
      headerName: t('laUnit'),
      minWidth: 150,
    },
    {
      field: 'eventType',
      headerName: t('userActivityEventType'),
      minWidth: 60,
    },
    {
      field: 'targetType',
      headerName: t('userActivityTargetType'),
      minWidth: 120,
    },
    {
      field: 'targetName',
      headerName: t('userActivityTargetName'),
      minWidth: 250,
      renderCell: ({ value, row }) => {
        switch (row.meta.eventData.data.target) {
          case UserActivityEventPayloadDtoTarget.Employee:
            const { employee, unit } = resolveEmployeeAndUnit(
              row.meta.eventData.data.targetId,
            );

            return !!employee && !!unit ? (
              <EmployeeName employee={employee} unit={unit} />
            ) : (
              value
            );
          default:
            return value;
        }
      },
    },
    {
      field: 'resourceName',
      headerName: t('userActivityTargetResource'),
      minWidth: 200,
    },
    {
      field: 'serverTime',
      type: 'dateTime',
      headerName: t('serverTime'),
      minWidth: 160,
      renderCell: ({ value }) => {
        return moment(value).format('L LTS');
      },
    },
    {
      field: 'clientTime',
      type: 'dateTime',
      headerName: t('clientTime'),
      minWidth: 160,
      renderCell: ({ value }) => {
        return moment(value).format('L LTS');
      },
    },
    {
      field: 'ipAddress',
      headerName: t('ipAddress'),
      minWidth: 110,
    },
    {
      field: 'userAgent',
      headerName: t('userAgent'),
      minWidth: 200,
    },
  ];

  const columnGroupingConfig: GridColumnGroupingModel = [
    {
      groupId: t('user'),
      children: [{ field: 'userName' }, { field: 'userUnit' }],
    },
    {
      groupId: t('activity'),
      children: [
        { field: 'eventType' },
        { field: 'resourceName' },
        { field: 'targetType' },
        { field: 'targetName' },
      ],
    },
    {
      groupId: t('server'),
      children: [{ field: 'serverTime' }],
    },
    {
      groupId: t('clientApplication'),
      children: [
        { field: 'clientTime' },
        { field: 'ipAddress' },
        { field: 'userAgent' },
      ],
    },
  ];

  return (
    <Box sx={{ height: '100%', display: 'grid', gridTemplateRows: 'auto 1fr' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `auto 1fr auto`,
          gridTemplateRows: '1fr',
          gridTemplateAreas: `"range tabs total"`,
          alignItems: 'center',
          padding: theme.spacing(1, 3),
          backgroundColor: '#eee',
        }}
      >
        <DateRangePicker
          onChange={onRangeChange}
          value={dateRange}
          maxDate={moment().toDate()}
          renderInput={(startProps, endProps) => (
            <Box
              sx={{
                gridArea: 'range',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, auto)',
                alignItems: 'center',
              }}
            >
              <TextField
                label={t('startDate')}
                {...startProps}
                size={'small'}
                sx={{ maxWidth: '120px' }}
              />
              <Box>&nbsp;-&nbsp;</Box>
              <TextField
                label={t('endDate')}
                {...endProps}
                size={'small'}
                sx={{ maxWidth: '120px' }}
              />
            </Box>
          )}
        />
        <Box sx={{ gridArea: 'tabs' }}></Box>
        <Box sx={{ margin: theme.spacing(0, 1), gridArea: 'total' }}>{`${t(
          'totalRows',
        )} ${gridData.length}`}</Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          '& .MuiDataGrid-root': { borderRadius: 0 },
        }}
      >
        <SolaForceDataGrid
          columns={columnsConfig}
          rows={gridData}
          columnGroupingModel={columnGroupingConfig}
          experimentalFeatures={{ columnGrouping: true }}
          hideFooter={true}
        />
      </Box>
    </Box>
  );
};
